import React, { useState } from 'react';
import { Table, Pagination } from 'antd';
import type { TableColumnsType, TableProps } from 'antd';

type TableRowSelection<T extends object = object> =
  TableProps<T>['rowSelection'];

interface CustomTableProps<T extends object> {
  dataSource: T[];
  columns: TableColumnsType<T>;
  selectedRowKeys?: React.Key[];
  onSelectChange?: (newSelectedRowKeys: React.Key[]) => void;
  totalElements?: number; // Total records
  totalPages?: number; // Total pages
  currentPage?: number; // Current page
  onPageChange?: (page: number, pageSize: number) => void; // Page change handler
  includePagination?: boolean; // Show pagination
  loading?: boolean;
  scrollY?: number;
  onRow?: (record: T, index?: number) => React.HTMLAttributes<any>;
  onChange?: TableProps<T>['onChange'];
}

const CustomTable = <T extends object>({
  dataSource,
  columns,
  selectedRowKeys,
  onSelectChange,
  totalElements = 0,
  currentPage = 1,
  onPageChange,
  includePagination,
  loading,
  scrollY,
  onRow,
  onChange,
}: CustomTableProps<T>) => {
  const [pageSize, setPageSize] = useState<number>(10);
  const rowSelection: TableRowSelection<T> = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
      {
        key: 'odd',
        text: 'Select Odd Row',
        onSelect: (changeableRowKeys) => {
          const newSelectedRowKeys = changeableRowKeys.filter(
            (_, index) => index % 2 === 0
          );
          if (onSelectChange) onSelectChange(newSelectedRowKeys);
        },
      },
      {
        key: 'even',
        text: 'Select Even Row',
        onSelect: (changeableRowKeys) => {
          const newSelectedRowKeys = changeableRowKeys.filter(
            (_, index) => index % 2 !== 0
          );
          if (onSelectChange) onSelectChange(newSelectedRowKeys);
        },
      },
    ],
  };

  const pagObj = {
    current: currentPage,
    total: totalElements,
    pageSize,
    onChange: (page: number, newPageSize: number) => {
      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
      }
      if (onPageChange) onPageChange(page, newPageSize);
    },
    showSizeChanger: true,
    pageSizeOptions: ['5', '10', '20', '50'],
    showTotal: (total: number) => `Total ${total} items`,
  };
  const pagination = includePagination ? pagObj : false;

  return (
    <>
      <Table<T>
        rowSelection={
          selectedRowKeys && onSelectChange ? rowSelection : undefined
        }
        onChange={onChange}
        columns={columns}
        dataSource={dataSource}
        pagination={pagination}
        loading={loading}
        onRow={onRow ? (record) => onRow(record) : undefined}
        components={{
          header: {
            cell: (props) => (
              <th {...props} style={{ fontFamily: 'Poppins' }} />
            ),
          },
          body: {
            row: (props) => (
              <tr {...props} style={{ cursor: onRow ? 'pointer' : 'default' }} />
            ),
            cell: (props) => (
              <td {...props} style={{ fontFamily: 'Poppins' }} />
            ),
          },
        }}
      />
    </>
  );
};

export default CustomTable;
